import React, { useState, useEffect, useRef} from 'react';
import { BarChart, ScatterChart, Bar, Scatter, Cell, XAxis, YAxis, Label, LabelList, Tooltip, ResponsiveContainer } from 'recharts';
import {firestore, timestamp} from './firebaseConfig';
import { useSessionReactions, useSessionColors, useGraphOn } from './reusableHooks';
import {getUniqueUserbyReaction } from './graph_display';

const useData = (sessionId) => {
    const [data,setData] = useState([]);
    useEffect(() => {
       const unsubscribe = firestore
            .collection("meetings/" + sessionId + "/voteCount").orderBy("reaction")
            .onSnapshot(async (snapshot) => {
                const newData = snapshot.docs.map(async (doc) => ({
                    //id: doc.id,
                    option_name: doc.data().reaction,
                    option_count: doc.data().count,
                    unique: await getUniqueUserbyReaction(sessionId, doc.data().reaction)
                }))
                //newData.sort(compareBarOrder);
                await Promise.all(newData)
                setData(await Promise.all(newData));
            })
        return () => unsubscribe();  
    }, [sessionId]) 
    
    return data;
}

//fetches organizer tags 
const useOData = (sessionId) => {
    const [data,setData] = useState([]);
    const mountedRef = useRef(false);

    useEffect(() => {
        mountedRef.current = true;

        const unsubscribe = firestore
            .collection("meetings/" + sessionId + "/topicStamps").orderBy("timestamp")
            .onSnapshot((snapshot) => {    
                const newData = snapshot.docs.map((doc) => ({
                    //id: doc.id,
                    timestamp: doc.data().timestamp.seconds,
                    topic: doc.data().topic, 
                    count: 1
                }))
                //newData.sort((a,b) => {return a.timestamp - b.timestamp})
                if(mountedRef.current) setData(newData);

            })
        return () => {
            unsubscribe(); 
            mountedRef.current = false
        };     
    }, [sessionId]) 

    //console.log(data)
    return data;
}
  
const toPercent = (num,total)  => {
    const percent = (num/total)*100;
    return Math.round(percent*100)/100;
}

const isVowel = (string) => {
    if (string.length > 0){
        const vowels = ['a', 'e', 'i', 'o', 'u'];
        return vowels.includes(string[0]);
    }
    return false;
}

function customTooltip({active, payload, label}){
    return (
        <>
        {active && payload && <div style={{height: "auto", position: "absolute", top: "-1rem"}}>
                    {/*<h6>{new Date(1000 * payload[0]?.payload.timestamp).toLocaleTimeString()}</h6>*/}
                    <h6>An organizer thinks this is 
                        {isVowel(payload[0]?.payload.topic.toLowerCase())? " an " : " a "} 
                        <strong>{payload[0]?.payload.topic.toLowerCase()}</strong>
                    </h6>
                </div>
        }
        </>
    )
}

function OrganizerTags({sessionId, interval, parent}){
    const oData = useOData(sessionId, interval);
    const organizerTags = useSessionReactions(sessionId, true);
    const barColors = ["#E41A1C", "#FF7F00", "#377EB8", "#984EA3", "#4DAF4A", "#404142"];
    const COLORS = {};
    organizerTags?.map((reaction, index) => {
        COLORS[reaction] = barColors[index % barColors.length];
    })

    return (
    <ResponsiveContainer width="85%" height="40%">
        <ScatterChart width="100%" height="100%" data={oData} margin={{ top: 0, left: 0, right: 0, bottom: -10 }}>
            <YAxis dataKey="count" domain={[1,1]} hide/>
            <XAxis dataKey="timestamp" domain={[null]} textAnchor="middle" tick={{fontSize: '0.65rem', fill: '#000000', dx:3}} angle={0} 
                tickFormatter={(timestamp) => {
                    if(timestamp) return new Date(1000 * timestamp).toLocaleTimeString();
                    else return "";
                    }}>
                <Label value="Organizer Tags" offset={150} position="insideBottom" fontSize="0.75rem"/>
            </XAxis>
            <Tooltip content={customTooltip}/>
            <LabelList dataKey="timestamp" position="top" formatter={(value) => {return value + '%';}} fontSize="0.65rem" fill="black"/>
            <Scatter name="Sentiment" data={oData} fill="#8884d8" isAnimationActive={false}>
                {oData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[entry["topic"]]} />
                ))}
            </Scatter>
        </ScatterChart>
    </ResponsiveContainer>
    );
}

function ParticipantTags({sessionId, interval, parent, windowSize}){
    const data = useData(sessionId);
    const participantTags = useSessionReactions(sessionId, false);
    //const barColors = ["#E41A1C", "#FF7F00", "#377EB8", "#984EA3", "#4DAF4A", "#404142"];
    const barColors = useSessionColors(sessionId, false);
    const COLORS = {};
    participantTags?.map((reaction, index) => {
        COLORS[reaction] = barColors[index % barColors.length];
    })

    /*const total = data.reduce((sum,obj) => sum+(obj["option_count"]||0),0);
    const dataInPercent = data.map((obj) => ({ option_name: obj.option_name, option_count: toPercent(obj.option_count,total), uniqueInPercent: toPercent(obj.unique, total), unique: obj.unique}) ); */
    const max = data.reduce((a,b) => ( (a.option_count > b.option_count) ? a : b), 0 ).option_count;
    const bars = [];

    data.forEach((bar, i) => {
        bars.push(<Cell key={`cell-${bar.option_name}`} fill={COLORS[bar.option_name]}/>);
    })

    let barSize = 50;
    if(windowSize?.width >= 870) barSize = 70
    if(windowSize?.width >= 960) barSize = 80
    if(windowSize?.width >= 1280) barSize = 100
    if(windowSize?.width >= 1480) barSize = 120
    if(windowSize?.width >= 1700) barSize = 140
    if(windowSize?.width >= 1920) barSize = 140
    if(windowSize?.width >= 2000) barSize = 160

    return (
        <ResponsiveContainer width="85%" height="100%">
            <BarChart barGap={-barSize} width="100%" height="100%" data={data} margin={{ top: 12, left: 0, right: 0, bottom: -10 }}>
                <YAxis type="number" domain={[0, max]} hide/>
                {parent !== "dashboard" && <XAxis dataKey="option_name" textAnchor="middle" tick={{fontSize: '0.7rem', fill: '#000000'}} interval={0} angle={0}>
                    <Label value="Participant Reactions" offset={-5} position="insideBottom" fontSize="0.75rem"/>
                </XAxis>}
                {parent === "dashboard" && <XAxis dataKey="option_name" textAnchor="middle" tick={{fontSize: '0.9rem', fill: '#000000'}} interval={0} angle={0}>
                    <Label value="Participant Reactions" offset={-20} position="insideBottom" fontSize="0.95rem"/>
                </XAxis>}
                <Bar dataKey="option_count" barSize={barSize} fill="grey" fillOpacity={0.8} isAnimationActive={false}>
                    {parent !== "dashboard" && <LabelList dataKey="option_count" position="top" /*formatter={(value) => {return value + '%';}}*/ fontSize="0.8rem" fill="black"/>}
                    {parent === "dashboard" && <LabelList dataKey="option_count" position="top" offset={10} /*formatter={(value) => {return value + '%';}}*/ fontSize="1rem" fill="black" style={{fontWeight: "bold"}}/>}
                    {/*parent === "dashboard" && <LabelList dataKey="unique" position="top" offset={5} formatter={(value) => {return '(' + value + ' unique)';}} fontSize="0.9rem" fill="black" style={{fontWeight: "normal"}}/>*/}
                    { 
                        bars.map((entry, index) => (
                            bars[index]
                        ))
                    }
                </Bar>
                {parent === "dashboard"  && 
                    <Bar dataKey="unique" barSize={barSize} fill="#8884d8" fillOpacity={1} isAnimationActive={false}>
                    { 
                        bars.map((entry, index) => (
                            bars[index]
                        ))
                    }
                        <LabelList dataKey="unique" position="center" formatter={(value) => {return '(' + value + ' attendees)';}} fontSize="0.9rem" fill="black" style={{fontWeight: "normal"}}/>
                </Bar>}
            </BarChart>
        </ResponsiveContainer>
    )
}


function UniqueParticipantTags({sessionId, interval, parent, windowSize}){
    const data = useData(sessionId);
    const participantTags = useSessionReactions(sessionId, false);
    const barColors = useSessionColors(sessionId, false);
    const COLORS = {};
    participantTags?.map((reaction, index) => {
        COLORS[reaction] = barColors[index % barColors.length];
    })

    const max = data.reduce((a,b) => ( (a.unique > b.unique) ? a : b), 0 ).unique;
    const bars = [];

    data.forEach((bar, i) => {
        bars.push(<Cell key={`cell-${bar.option_name}`} fill={COLORS[bar.option_name]}/>);
    })

    return (
        <ResponsiveContainer width="85%" height="100%">
            <BarChart width="100%" height="100%" data={data} margin={{ top: 12, left: 0, right: 0, bottom: -10 }}>
                <YAxis type="number" domain={[0, max]} hide/>
                {parent === "dashboard" && <XAxis dataKey="option_name" textAnchor="middle" tick={{fontSize: '0.9rem', fill: '#000000'}} interval={0} angle={0}>
                    <Label value="Unique Participant Reactions" offset={-20} position="insideBottom" fontSize="0.95rem"/>
                </XAxis>}
                {parent === "dashboard"  && 
                    <Bar dataKey="unique" fill="#8884d8" fillOpacity={1} isAnimationActive={false}>
                    { 
                        bars.map((entry, index) => (
                            bars[index]
                        ))
                    }
                        <LabelList dataKey="unique" position="top" offset={10} fontSize="1rem" fill="black" style={{fontWeight: "bold"}}/>
                </Bar>}
            </BarChart>
        </ResponsiveContainer>
    )
}

export default function Stats({sessionId, isAdmin, parent, windowSize, uniqueOnly}) {
    const height = (parent === "dashboard")? "h-75" : "";
    const sessionGraphOn = useGraphOn(sessionId); 
    const LAST_MINUTE = () => (timestamp.fromMillis((timestamp.now().seconds*1000)-60000));
    const [interval, setNewInterval] = useState(LAST_MINUTE());
    const mountedRef = useRef(false);

    const [currentGraph, setGraph] = useState("participant");
    const switchGraphs = () => {
        if(currentGraph === "participant" && mountedRef.current) setGraph("organizer");
        else if(currentGraph === "organizer" && mountedRef.current) setGraph("participant");
    }

    //refreshes component every 60 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            if(mountedRef.current) setNewInterval(LAST_MINUTE());
          return () => clearInterval(timer)
        }, 60000);
    },[])

    //cleanup 
    useEffect(() => {
        mountedRef.current = true;
    
        return () => { mountedRef.current = false; };
    }, []);

    return (
        <>
        {sessionGraphOn && isAdmin && parent !== "dashboard" &&
        <div id="stats" className={"mt-4 mb-4 " +  height} style={{position:"relative"}}>
            {currentGraph === "participant" && <ParticipantTags sessionId={sessionId} interval={interval} parent={parent} uniqueOnly={false}/>}
            {currentGraph === "organizer" && <OrganizerTags sessionId={sessionId} interval={interval} />}
            <i className="fas fa-chevron-right" style={{position:"absolute", right: 5, top: "40%", fontSize:"1.5rem", cursor:'pointer'}} onClick={() => switchGraphs()}></i>
        </div>
        }
        {isAdmin && parent === "dashboard" &&
        <div id="stats" className={"mt-4 mb-4 " +  height} style={{position:"relative"}}>
           {uniqueOnly? <UniqueParticipantTags sessionId={sessionId} interval={interval} parent={parent} windowSize={windowSize}/> : 
           <ParticipantTags sessionId={sessionId} interval={interval} parent={parent} windowSize={windowSize}/>}
        </div>
        }
        {sessionGraphOn && !isAdmin &&
        <div id="stats" className={"mt-4 mb-4 " +  height} style={{position:"relative"}}>
            <ParticipantTags sessionId={sessionId} interval={interval} parent={parent} uniqueOnly={false}/>
        </div>
        }
        </>
    )
}
